// Labels and formatters shared by the Test Data Management screens.

export const ENGINE_BADGE = {
  postgresql: 'PG', postgres: 'PG', mysql: 'MY', mariadb: 'MA', mssql: 'MS', sqlserver: 'MS', oracledb: 'OR', oracle: 'OR',
  mongodb: 'MG', salesforce: 'SF', snowflake: 'SN', db2: 'D2', s3: 'S3', azure_blob: 'AZ', sftp: 'FT',
};

export const engineBadge = (engine) => ENGINE_BADGE[String(engine || '').toLowerCase()] || String(engine || '??').slice(0, 2).toUpperCase();

/**
 * One key per engine, whatever a connection calls it: the provision screen matches a staged
 * source to a target by engine, and "postgres" and "postgresql" are the same engine.
 */
export const ENGINE_ALIAS = { postgres: 'postgresql', sqlserver: 'mssql', oracle: 'oracledb' };
export const engineKey = (engine) => {
  const e = String(engine || '').toLowerCase().trim();
  return ENGINE_ALIAS[e] || e;
};

/** A subset whose runs are masked before they can be provisioned (every mode but NONE). */
export const isMaskedSubset = (s) => Boolean(s) && String(s.maskingMode || 'PLATFORM') !== 'NONE';

/** The source the selection runs against. */
export const parentOf = (subset) => ((subset && subset.sources) || []).find((m) => m.parent) || null;

/** How provisioned rows are written. Provisioning never truncates a target: rows are added, or updated-or-added. */
export const WRITE_MODES = [
  ['APPEND', 'Append — add rows, leave what is there'],
  ['UPSERT', 'Upsert — update on primary key, insert the rest (PostgreSQL / MySQL)'],
];

/**
 * Which connections TDM can read from or write to. The worker decides for real; this only keeps
 * the picker honest, and it is ONE rule for sources and targets so the two screens cannot drift.
 *
 * Relational over JDBC, MongoDB (TDM F1) and Salesforce (TDM M4) — a NoSQL source that is not
 * Mongo, or a SaaS source that is not Salesforce, is refused by the platform at the group.
 * `ownerRef` rows are the platform's own hidden raw-view sources and are never a user's choice.
 */
export const SUPPORTED_ENGINES = ['mongodb'];
export const SUPPORTED_SAAS_ENGINES = ['salesforce'];

export const isTdmConnection = (c) => {
  if (!c || c.ownerRef) return false;
  const type = (c.datasourceType || 'RELATIONAL').toUpperCase();
  if (type === 'RELATIONAL') return true;
  if (type === 'NOSQL') return SUPPORTED_ENGINES.includes(String(c.type || c.engine || '').toLowerCase());
  if (type === 'SAAS') return SUPPORTED_SAAS_ENGINES.includes(String(c.type || c.engine || '').toLowerCase());
  return false;
};

/** Subset kind the platform expects for a parent of this engine (it derives the same and rejects a mismatch). */
export const kindForEngine = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'SALESFORCE';
  if (e === 'mongodb') return 'MONGO';
  return 'RDBMS';
};

/**
 * Where a provision writes on a target, as the platform resolves it from the target data source
 * (`TdmTargetRules.schemaOf`): a relational target's own schema, a MongoDB target's database, and
 * nothing for a Salesforce org (null). `value` null = the connection names none, so each table goes
 * to the schema it was staged from ("same as source"). Read-only — it is not chosen per run.
 */
export const targetWriteScope = (engine, conn) => {
  const kind = kindForEngine(engine);
  if (kind === 'SALESFORCE') return null;
  const raw = kind === 'MONGO' ? conn && conn.database : conn && (conn.schemaName != null ? conn.schemaName : conn.schema);
  const value = raw != null && String(raw).trim() ? String(raw).trim() : null;
  return { noun: kind === 'MONGO' ? 'database' : 'schema', value };
};

/** What one row is called per kind — Salesforce records, Mongo documents, SQL rows. */
export const unitNoun = (kind) => {
  switch (String(kind || 'RDBMS').toUpperCase()) {
    case 'SALESFORCE': return 'records';
    case 'MONGO': return 'documents';
    default: return 'rows';
  }
};

/** What the start "table" is called per kind. */
export const tableNoun = (kind) => (String(kind || '').toUpperCase() === 'SALESFORCE' ? 'object' : 'table');

/** A target must additionally be writable — provisioning inserts rows. */
export const isTdmTarget = (c) => isTdmConnection(c) && !c.readOnly;

/** isTdmConnection as a shared-picker restriction, saying why a row is left out. */
export const TDM_CONNECTION_RULE = {
  label: 'Relational, MongoDB or Salesforce',
  test: isTdmConnection,
  why: (c) => (c.ownerRef ? 'Managed by TDM' : `${engineLabel(c)} is not supported by Test Data Management`),
};

/** isTdmTarget as picker restrictions: TDM-capable, then writable. */
export const TDM_TARGET_RULES = [
  TDM_CONNECTION_RULE,
  { label: 'Writable only', test: (c) => !c.readOnly, why: () => 'Read-only: deploying writes rows and can never target one' },
];

/** The engine as the picker shows it: the connection's own word, upper-cased, never guessed. */
export const engineLabel = (c) => String((c && (c.type || c.engine)) || '').toUpperCase() || '—';

export const isMongo = (engine) => String(engine || '').toLowerCase() === 'mongodb';
export const isSalesforce = (engine) => String(engine || '').toLowerCase() === 'salesforce';

export const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

export const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export const fmtBytes = (b) => {
  if (b == null) return '—';
  const n = Number(b);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const RUN_STATUS_TONE = {
  PENDING: 'neutral', RUNNING: 'info', SUCCEEDED: 'success', FAILED: 'danger', CANCELLED: 'warning', SKIPPED: 'neutral',
};

export const STEP_LABEL = { SUBSET: 'Stage', DISCOVER: 'Discover', MASK: 'Mask', GATE: 'Gate', DEPLOY: 'Deploy' };

/**
 * Masking providers (GET /api/v1/providers?type=MASKING) per masking mode — the same rule the
 * platform applies at subset save (TdmMaskDispatchRules). PLATFORM dispatches to the C2 masking
 * service (no `vendor`); the two Delphix modes dispatch to a Delphix registration (vendor
 * `delphix`, served through `integrations`). Anything else is not offered.
 */
export const isDispatchableMaskingEngine = (p) => Boolean(p) && !(p.vendor && String(p.vendor).trim());
export const isDelphixEngine = (p) => Boolean(p) && String(p.vendor || '').trim().toLowerCase() === 'delphix';
export const isDelphixMode = (mode) => mode === 'DELPHIX_MANAGED' || mode === 'DELPHIX_EXISTING_JOB';
export const enginesForMode = (providers, mode) => (providers || []).filter((p) => (isDelphixMode(mode) ? isDelphixEngine(p) : isDispatchableMaskingEngine(p)));

/**
 * Staging types each masking mode can stage to. Platform masking is inline (TDM contract 2.0: the
 * worker masks rows before staging them and never reads staging back), so every type. A Delphix
 * engine's file connectors: S3, Azure Blob, SFTP (password) — never the worker's LOCAL disk.
 */
export const PLATFORM_STAGING_TYPES = ['S3', 'SFTP', 'AZURE_BLOB', 'LOCAL'];
export const DELPHIX_STAGING_TYPES = ['S3', 'AZURE_BLOB', 'SFTP'];
export const maskableStagingTypes = (mode) => (isDelphixMode(mode) ? DELPHIX_STAGING_TYPES : PLATFORM_STAGING_TYPES);
export const isMaskableStaging = (st, mode = 'PLATFORM') => Boolean(st) && maskableStagingTypes(mode).includes(String(st.type || '').toUpperCase());

export const MASKING_MODE_LABEL = {
  PLATFORM: 'Platform masking', DELPHIX_MANAGED: 'Delphix', DELPHIX_EXISTING_JOB: 'Delphix (existing job)', NONE: 'No masking',
};

/** Where the masking inventory comes from, per inventory mode. */
export const INVENTORY_MODE_LABEL = {
  DISCOVER: 'Discover first', PROFILE: 'Manual inventory', SOURCE_DISCOVERY: 'Latest discovery',
};

/** "Platform masking · Discover first" for a subset card; modes without an inventory show the mode alone. */
export const maskingLabel = (subset) => {
  if (!subset) return '';
  const mode = String(subset.maskingMode || 'PLATFORM').toUpperCase();
  const modeLabel = MASKING_MODE_LABEL[mode] || mode;
  if (mode === 'NONE') return modeLabel;
  if (mode === 'DELPHIX_EXISTING_JOB') return `${modeLabel}${subset.externalMaskingJobId ? ` #${subset.externalMaskingJobId}` : ''}`;
  const inv = String(subset.inventoryMode || 'DISCOVER').toUpperCase();
  return `${modeLabel} · ${INVENTORY_MODE_LABEL[inv] || inv}`;
};

/** "Subset run · 9/14 9:41 AM · SUCCEEDED" or the failure reason when the run failed. */
export const runSummary = (run) => {
  if (!run) return 'No runs yet';
  const base = `Run · ${fmtWhen(run.startedAt)} · ${run.status}`;
  if (run.status === 'FAILED' && run.error) return `${base} · ${run.error}`;
  if (run.status === 'RUNNING' && run.currentStep) return `${base} · ${STEP_LABEL[run.currentStep] || run.currentStep}`;
  return base;
};

export const rowsLabel = (run, kind) => {
  const rows = run && run.totals ? run.totals.rows : null;
  return rows == null ? null : `${fmtNum(rows)} ${unitNoun(kind)}`;
};

/** Selection description for a subset card. */
export const selectionLabel = (subset) => {
  const sel = subset && subset.selection ? subset.selection : {};
  const kind = subset && subset.kind;
  const table = sel.startTable || tableNoun(kind);
  switch ((sel.mode || 'ALL').toUpperCase()) {
    case 'SEEDS': return `${fmtNum((sel.seeds || []).length)} seed ${sel.seedColumn || 'id'}s of ${table}`;
    case 'WHERE': return `${table} where ${sel.where || '…'}`;
    default: return `all ${unitNoun(kind)} of ${table}`;
  }
};

// The budget is a size (2026-10-21). A row budget only survives on subsets saved before then.
export const budgetLabel = (subset) => {
  if (!subset) return '';
  if (subset.budgetMaxBytes) return `budget ${fmtBytes(subset.budgetMaxBytes)}`;
  if (subset.budgetMaxRows) return `budget ${fmtNum(subset.budgetMaxRows)} ${(subset.budgetUnit || 'ROWS').toLowerCase()}`;
  return 'no budget';
};

export const SIZE_UNITS = { MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };

/** A byte count as the editor shows it: whole GB when it is one, else MB (at most two decimals). */
export const bytesToSize = (bytes) => {
  const n = Number(bytes);
  if (!bytes || !Number.isFinite(n) || n <= 0) return { value: '', unit: 'MB' };
  if (n % SIZE_UNITS.GB === 0) return { value: String(n / SIZE_UNITS.GB), unit: 'GB' };
  return { value: String(Math.round((n / SIZE_UNITS.MB) * 100) / 100), unit: 'MB' };
};

/** The editor's size field as the byte count the platform stores; null when empty or not positive. */
export const sizeToBytes = (value, unit) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * (SIZE_UNITS[unit] || SIZE_UNITS.MB));
};

/**
 * Salesforce sandbox data-storage limits, offered as budget presets for a Salesforce parent: the
 * budget is already in the org's storage accounting, the unit these limits are measured in. A Full
 * sandbox has production's storage, so it is no cap (null — an empty budget).
 */
export const SALESFORCE_SANDBOXES = [
  { key: 'DEVELOPER', label: 'Developer · 200 MB', bytes: 200 * SIZE_UNITS.MB },
  { key: 'DEVELOPER_PRO', label: 'Developer Pro · 1 GB', bytes: SIZE_UNITS.GB },
  { key: 'PARTIAL_COPY', label: 'Partial Copy · 5 GB', bytes: 5 * SIZE_UNITS.GB },
  { key: 'FULL', label: 'Full / unlimited · no cap', bytes: null },
];

/** The sandbox preset a stored budget matches (no budget = FULL); null when it is a custom size. */
export const sandboxForBytes = (bytes) => {
  const n = Number(bytes);
  if (!bytes || !Number.isFinite(n) || n <= 0) return 'FULL';
  const hit = SALESFORCE_SANDBOXES.find((s) => s.bytes === n);
  return hit ? hit.key : null;
};

/**
 * What a byte of budget means per kind — the worker prices each staged row the same way the
 * preview's bytes are estimated: Salesforce by the org's storage accounting, everything else by
 * the row's size at the source. Never the CSV the row becomes.
 */
export const budgetBasis = (kind) => (String(kind || 'RDBMS').toUpperCase() === 'SALESFORCE'
  ? 'Salesforce storage: 2 KB a record (4 KB a person account, more for some objects) — the unit the target org\'s data storage limit is measured in.'
  : 'Size at the source, as discover measured it on disk — not the size of the staged files.');

/**
 * Seed ids typed or loaded from a file → array: one per line, or separated by commas, semicolons or
 * tabs (so a one-column CSV / TSV export works), CRLF line ends and "quoted" cells included.
 */
export const parseSeeds = (text) => String(text || '').split(/[\r\n,;\t]+/)
  .map((s) => s.trim().replace(/^"(.*)"$/, '$1').trim()).filter(Boolean);

/** The ids once each, first occurrence order kept, plus how many repeats were dropped. */
export const uniqueSeeds = (ids) => {
  const seen = new Set();
  const out = [];
  (ids || []).forEach((id) => { if (!seen.has(id)) { seen.add(id); out.push(id); } });
  return { ids: out, duplicates: (ids || []).length - out.length };
};

/** A seed file larger than this is refused before it is read (100K long ids are a few MB). */
export const SEED_FILE_MAX_BYTES = 20 * 1024 * 1024;

export const slugPreview = (name) => String(name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'item';

/** Staging root for display: strip the scheme noise, keep bucket/prefix. */
export const stagingLabel = (st) => (st ? `${st.name} · ${st.rootPath}` : '—');
